import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import ConfirmButton, {
  ConfirmButtonTransitionState
} from "@saleor/components/ConfirmButton";
import Form from "@saleor/components/Form";
import SingleSelectField from "@saleor/components/SingleSelectField";
import { maybe } from "@saleor/misc";
import { DialogProps } from "@saleor/types";
import { SettlementStatusEnum } from "@saleor/types/globalTypes";
import React from "react";

import { PayoutFinanceAdjustment_payoutFinanceAdjustment } from "../../types/PayoutFinanceAdjustment";
import { checkSettlementStatus } from "../../utils/handlers";
import { restrictInput } from "./../../views/FinanceOrderList/handlers";

export const otherAdjustmentRemarksCharacterLimit: number = 250;

export interface AdjustDialogForm {
  deliveryFeeAdjustment: string;
  otherAdjustment: string;
  otherAdjustmentRemarks: string;
  paymentFeeAdjustment: string;
  penalties: string;
  settlementStatus: SettlementStatusEnum;
}
interface FinanceAdjustDialogProps extends DialogProps {
  updateOpenOrders: (formData: AdjustDialogForm) => void;
  payoutFinanceAdjustments?: PayoutFinanceAdjustment_payoutFinanceAdjustment;
  buttonState: ConfirmButtonTransitionState;
}

const useStyles = makeStyles(
  theme => ({
    form: {
      "& .MuiFormControl-root": {
        marginBottom: "1.25em",
        marginTop: "0.5em",
        [theme.breakpoints.up("lg")]: {
          marginBottom: "0.5em",
          marginTop: "1.5em"
        }
      },
      "& .MuiOutlinedInput-input": {
        paddingBottom: "16.5px",
        paddingTop: "16.5px"
      },
      "& .MuiOutlinedInput-multiline": {
        paddingTop: 0
      },
      "& label": {
        overflow: "initial",
        width: "initial"
      }
    },
    gridContainer: {
      alignItems: "center",
      ...{
        "& .MuiGrid-item .MuiTypography-root": {
          marginBottom: 0,
          marginTop: "0.75em",
          [theme.breakpoints.up("lg")]: {
            marginBottom: "0.5em",
            marginTop: "1.5em"
          }
        }
      }
    },
    otherAdjustmentRemarks: {
      "& .MuiInputBase-root": {
        paddingBottom: 0
      },
      "& label": {
        marginTop: "2px"
      },
      "& textarea": {
        minHeight: "5em"
      }
    },
    otherAdjustmentRemarksLabel: {
      display: "block",
      marginBottom: "0.5em",
      textAlign: "right",
      width: "100%"
    }
  }),
  {
    name: "FinanceAdjustDialog"
  }
);

const FinanceAdjustDialog: React.FC<FinanceAdjustDialogProps> = props => {
  const {
    open,
    onClose,
    updateOpenOrders,
    payoutFinanceAdjustments,
    buttonState
  } = props;
  const isLoading = buttonState === "loading";
  const classes = useStyles(props);
  const initialValues = {
    deliveryFeeAdjustment: payoutFinanceAdjustments?.delivery_fee_adjustment?.toFixed(
      2
    ),
    otherAdjustment: payoutFinanceAdjustments?.other_adjustment?.toFixed(2),
    otherAdjustmentRemarks: payoutFinanceAdjustments?.other_adjustment_remarks,
    paymentFeeAdjustment: payoutFinanceAdjustments?.payment_fee_adjustment?.toFixed(
      2
    ),
    penalties: payoutFinanceAdjustments?.penalties?.toFixed(2),
    settlementStatus: checkSettlementStatus(
      payoutFinanceAdjustments?.settlement_status
    )
  };

  const statusChoices = maybe(
    () => [
      {
        label: `For Payment`,
        value: SettlementStatusEnum.FOR_PAYMENT
      },
      {
        label: `Excluded`,
        value: SettlementStatusEnum.EXCLUDED
      }
    ],
    []
  );

  const handleSubmit = formData => {
    updateOpenOrders(formData);
  };

  const validateInput = (
    change,
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { target } = event;
    const { name, value } = target;

    if (restrictInput(name, value)) {
      change({
        target: {
          name,
          value
        }
      });
    }
  };

  return (
    <>
      <Dialog
        maxWidth="sm"
        fullWidth
        open={open}
        onClose={isLoading ? null : onClose}
        className={classes.form}
      >
        <DialogTitle>Adjustment Dialog</DialogTitle>
        <Form initial={initialValues} onSubmit={handleSubmit}>
          {({ data, change, submit }) => (
            <>
              <DialogContent>
                <Grid container className={classes.gridContainer}>
                  <Grid item xs={12} lg={4}>
                    <Typography>Status</Typography>
                  </Grid>
                  <Grid item xs={12} lg={8}>
                    <SingleSelectField
                      choices={statusChoices}
                      disabled={false}
                      placeholder="Select an option"
                      name="settlementStatus"
                      onChange={change}
                      value={data.settlementStatus || ""}
                    />
                  </Grid>

                  <Grid item xs={12} lg={4}>
                    <Typography>Payment Fee Adjustment</Typography>
                  </Grid>
                  <Grid item xs={12} lg={8}>
                    <TextField
                      name="paymentFeeAdjustment"
                      fullWidth
                      value={data.paymentFeeAdjustment}
                      onChange={e => validateInput(change, e)}
                    />
                  </Grid>

                  <Grid item xs={12} lg={4}>
                    <Typography>Delivery Fee Adjustment</Typography>
                  </Grid>
                  <Grid item xs={12} lg={8}>
                    <TextField
                      name="deliveryFeeAdjustment"
                      fullWidth
                      value={data.deliveryFeeAdjustment}
                      onChange={e => validateInput(change, e)}
                    />
                  </Grid>

                  <Grid item xs={12} lg={4}>
                    <Typography>Penalties</Typography>
                  </Grid>
                  <Grid item xs={12} lg={8}>
                    <TextField
                      name="penalties"
                      fullWidth
                      value={data.penalties}
                      onChange={e => validateInput(change, e)}
                    />
                  </Grid>

                  <Grid item xs={12} lg={4}>
                    <Typography>Others</Typography>
                  </Grid>
                  <Grid item xs={12} lg={8}>
                    <TextField
                      name="otherAdjustment"
                      fullWidth
                      value={data.otherAdjustment}
                      onChange={e => validateInput(change, e)}
                    />
                  </Grid>

                  <Grid item xs={12} lg={4}>
                    <Typography>Other Adjustment Remarks</Typography>
                  </Grid>
                  <Grid item xs={12} lg={8}>
                    <TextField
                      className={classes.otherAdjustmentRemarks}
                      name="otherAdjustmentRemarks"
                      fullWidth
                      multiline
                      value={data.otherAdjustmentRemarks}
                      onChange={e => validateInput(change, e)}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <label className={classes.otherAdjustmentRemarksLabel}>
                      {data?.otherAdjustmentRemarks?.length}/
                      {otherAdjustmentRemarksCharacterLimit}
                    </label>
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button fullWidth variant="contained" onClick={onClose}>
                  Back
                </Button>

                <ConfirmButton
                  disabled={isLoading || !data.settlementStatus}
                  transitionState={buttonState}
                  color="primary"
                  keepLabel={true}
                  variant="contained"
                  onClick={submit}
                >
                  Save
                </ConfirmButton>
              </DialogActions>
            </>
          )}
        </Form>
      </Dialog>
    </>
  );
};

export default FinanceAdjustDialog;
